/**
 * Collegamenti esterni del dettaglio titolo (SPEC-016 §4.3).
 *
 * Tabella statica MIC → sito ufficiale della borsa, stesso pattern di
 * `features/watchlist/openfigi-exchange-codes.ts` (F-047): solo domini
 * verificati, **mai un URL indovinato**. Le borse hanno formati di scheda
 * per-titolo non documentati e instabili: qui si linka la home ufficiale, e
 * l'unico link per-titolo è Yahoo Finance, il cui formato `quote/<simbolo>` è
 * lo stesso già usato dai provider del progetto. Un MIC non in tabella ricade
 * esplicitamente sul solo Yahoo Finance.
 *
 * Tutti i link vanno resi con `target="_blank" rel="noopener noreferrer"`.
 */

export type ExternalLink = {
  label: string;
  url: string;
  /** Dominio mostrato accanto all'etichetta, per far vedere DOVE si sta andando. */
  host: string;
};

/** Siti ufficiali dei mercati a catalogo (db/seeds/002-markets.sql). */
const MIC_OFFICIAL_SITES: Record<string, { name: string; url: string }> = {
  XMIL: { name: 'Borsa Italiana', url: 'https://www.borsaitaliana.it' },
  XETR: { name: 'Börse Frankfurt (Xetra)', url: 'https://www.boerse-frankfurt.de' },
  XLON: { name: 'London Stock Exchange', url: 'https://www.londonstockexchange.com' },
  XNYS: { name: 'New York Stock Exchange', url: 'https://www.nyse.com' },
  XNAS: { name: 'Nasdaq', url: 'https://www.nasdaq.com' },
  XPAR: { name: 'Euronext Paris', url: 'https://www.euronext.com' },
  XAMS: { name: 'Euronext Amsterdam', url: 'https://www.euronext.com' },
  XBRU: { name: 'Euronext Brussels', url: 'https://www.euronext.com' },
  XLIS: { name: 'Euronext Lisbon', url: 'https://www.euronext.com' },
  XDUB: { name: 'Euronext Dublin', url: 'https://www.euronext.com' },
  XSWX: { name: 'SIX Swiss Exchange', url: 'https://www.six-group.com' },
  XJPX: { name: 'Japan Exchange Group', url: 'https://www.jpx.co.jp' },
  XHKG: { name: 'Hong Kong Exchanges', url: 'https://www.hkex.com.hk' },
  XASX: { name: 'Australian Securities Exchange', url: 'https://www.asx.com.au' },
  XTSE: { name: 'Toronto Stock Exchange', url: 'https://www.tsx.com' },
  XSHG: { name: 'Shanghai Stock Exchange', url: 'https://english.sse.com.cn' },
  XSHE: { name: 'Shenzhen Stock Exchange', url: 'https://www.szse.cn' },
  // CRYPTO è uno pseudo-mercato (F-046): nessuna scheda ufficiale, resta Yahoo.
};

function hostOf(url: string): string {
  return new URL(url).host.replace(/^www\./, '');
}

/**
 * Link Yahoo per-titolo: `quote/<simbolo>` se il ticker Yahoo è risolto,
 * altrimenti la ricerca per ISIN — mai un simbolo inventato.
 */
export function yahooFinanceLink(providerSymbol: string | null, isin: string | null): ExternalLink | null {
  if (providerSymbol) {
    const url = `https://finance.yahoo.com/quote/${encodeURIComponent(providerSymbol)}`;
    return { label: 'Yahoo Finance', url, host: hostOf(url) };
  }
  if (isin) {
    const url = `https://finance.yahoo.com/lookup?s=${encodeURIComponent(isin)}`;
    return { label: 'Yahoo Finance (ricerca ISIN)', url, host: hostOf(url) };
  }
  return null;
}

export function buildExternalLinks(input: {
  providerSymbol: string | null;
  isin: string | null;
  marketMic: string | null;
}): { links: ExternalLink[]; fallbackOnly: boolean } {
  const links: ExternalLink[] = [];
  const yahoo = yahooFinanceLink(input.providerSymbol, input.isin);
  if (yahoo) links.push(yahoo);

  const official = input.marketMic ? MIC_OFFICIAL_SITES[input.marketMic] : undefined;
  if (official) {
    links.push({
      label: `${official.name} — sito ufficiale`,
      url: official.url,
      host: hostOf(official.url),
    });
  }

  // `fallbackOnly` dice alla UI di dichiarare il ripiego: un titolo senza
  // scheda pubblica del mercato mostra solo Yahoo, e la pagina lo dice.
  return { links, fallbackOnly: links.length > 0 && !official };
}
